// The search screen's state machine — the web twin of MessageSearchView.swift's @State block — kept
// pure so the two-pass rules are testable without a worker or a DOM:
//  - every keystroke resets the server flags and the LOCAL answer replaces the list;
//  - only the SERVER may claim "no results", and an EMPTY server answer never replaces hits this
//    device already found for the same query (cached-only notice instead — docs/PARITY.md Search,
//    user with a screenshot 2026-09-13: "search shows results then it hides them");
//  - a failed server pass keeps the local hits (they are real results) and says so;
//  - an answer for a query the reader has moved on from, or for a superseded request, is dropped.
import { ChannelListDerivation } from '../../../core/channelListDerivation';
import type { ChannelRecord, PostRecord, UserRecord } from '../../../core/records';
import { RelativeTime } from '../../../core/relativeTime';
import * as SearchQuery from '../../../core/searchQuery';
import * as SearchResultsPresentation from '../../../core/searchResultsPresentation';
import type { SearchBundle, ServerSearchOutcome } from '../../../data/features/search';
import { t } from '../../../i18n/i18n';

export interface SearchState {
  /** The (trimmed) query the list belongs to. */
  query: string;
  posts: PostRecord[];
  /**
   * The query `posts` answered. While the reader types, the previous answer stays on screen until
   * the index answers the new text (a few ms) — clearing it per keystroke flashed the hint between
   * every two letters. Only rows that belong to THIS query may veto an empty server answer.
   */
  postsFor: string;
  usersById: Record<string, UserRecord>;
  channelsById: Record<string, ChannelRecord>;
  participants: Record<string, string[]>;
  currentUserId: string;
  page: number;
  hasMore: boolean;
  /** A server request is in flight (its id). 0 = none. */
  serverRequest: number;
  /** The server answered this query at least once (the no-results gate). */
  searched: boolean;
  /** The SERVER pass could not run — distinct from "it ran and found nothing". */
  failed: boolean;
  /** The server found nothing and this device had something: the rows stay, with a notice. */
  cachedOnly: boolean;
  loadingMore: boolean;
}

export function initialState(query = ''): SearchState {
  return {
    query: query.trim(),
    posts: [],
    postsFor: '',
    usersById: {},
    channelsById: {},
    participants: {},
    currentUserId: '',
    page: 0,
    hasMore: false,
    serverRequest: 0,
    searched: false,
    failed: false,
    cachedOnly: false,
    loadingMore: false,
  };
}

export type SearchAction =
  /** The text changed: everything server-side about the previous text is void. */
  | { type: 'typed'; query: string }
  | { type: 'local'; query: string; bundle: SearchBundle }
  /** Fresh names (users / channels / participants) for the rows on screen; the rows stay. */
  | { type: 'names'; bundle: SearchBundle }
  | { type: 'serverStart'; query: string; request: number; nextPage: boolean }
  | { type: 'serverDone'; query: string; request: number; nextPage: boolean; outcome: ServerSearchOutcome };

function withBundle(state: SearchState, bundle: SearchBundle, posts: PostRecord[], merge: boolean): SearchState {
  const usersById = merge ? { ...state.usersById } : {};
  for (const u of bundle.users) usersById[u.id] = u;
  const channelsById = merge ? { ...state.channelsById } : {};
  for (const c of bundle.channels) channelsById[c.id] = c;
  return {
    ...state,
    posts,
    usersById,
    channelsById,
    participants: merge ? { ...state.participants, ...bundle.participants } : { ...bundle.participants },
    currentUserId: bundle.currentUserId || state.currentUserId,
  };
}

export function reduce(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'typed': {
      const query = action.query.trim();
      if (query === state.query) return state;
      if (query === '') return initialState('');
      return {
        ...initialState(query),
        posts: state.posts,
        postsFor: state.postsFor,
        usersById: state.usersById,
        channelsById: state.channelsById,
        participants: state.participants,
        currentUserId: state.currentUserId,
      };
    }
    case 'local': {
      if (action.query.trim() !== state.query) return state;
      // The server already answered THIS query: its list is the wider truth and stays.
      if (state.searched && !state.failed && !state.cachedOnly) return state;
      const next = withBundle(state, action.bundle, action.bundle.posts, false);
      return { ...next, postsFor: state.query, page: 0, hasMore: false };
    }
    case 'names': {
      const next = withBundle(state, action.bundle, state.posts, true);
      return { ...next, posts: state.posts };
    }
    case 'serverStart': {
      if (action.query.trim() !== state.query) return state;
      return { ...state, serverRequest: action.request, failed: action.nextPage ? state.failed : false, loadingMore: action.nextPage };
    }
    case 'serverDone': {
      if (action.query.trim() !== state.query || action.request !== state.serverRequest) return state;
      const settled = { ...state, serverRequest: 0, loadingMore: false };
      const outcome = action.outcome;
      if (outcome.kind === 'failed') {
        // The local hits stay on screen — throwing them away because the network failed would be
        // the second wrong answer in a row.
        if (action.nextPage) return { ...settled, hasMore: false };
        return { ...settled, failed: true, searched: true };
      }
      if (action.nextPage) {
        const seen = new Set(state.posts.map((p) => p.id));
        const unique = outcome.bundle.posts.filter((p) => !seen.has(p.id));
        const next = withBundle(settled, outcome.bundle, [...state.posts, ...unique], true);
        next.postsFor = state.query;
        // A full page suggests another behind it; a short one (or one with nothing new) is the end.
        return { ...next, page: state.page + 1, hasMore: outcome.hasMore && unique.length > 0 };
      }
      if (outcome.bundle.posts.length === 0 && state.posts.length > 0 && state.postsFor === state.query) {
        return { ...settled, cachedOnly: true, searched: true, failed: false, hasMore: false };
      }
      const next = withBundle(settled, outcome.bundle, outcome.bundle.posts, false);
      return { ...next, postsFor: state.query, page: 0, hasMore: outcome.hasMore, searched: true, failed: false, cachedOnly: false };
    }
  }
}

/** What the body shows — one answer, so the view has no state logic of its own. */
export type Phase = 'recents' | 'searching' | 'failed' | 'typedNotSubmitted' | 'empty' | 'results';

export function phase(state: SearchState, text: string): Phase {
  if (text.trim() === '') return 'recents';
  // Rows that belong to an older text count as "nothing yet" only once the index has answered
  // the new one (it always does, in a few ms) — until then they stay, so typing never flickers.
  if (state.posts.length === 0) {
    if (state.serverRequest !== 0) return 'searching';
    if (state.failed) return 'failed';
    if (!state.searched) return 'typedNotSubmitted';
    return 'empty';
  }
  return 'results';
}

/**
 * The one-line flattening is the costly part of a row (Markdown parse + emoji substitution), and the
 * same messages come back keystroke after keystroke — memoised by message text.
 */
const flattened = new Map<string, string>();
function flattenForOneLine(message: string): string {
  let out = flattened.get(message);
  if (out === undefined) {
    out = ChannelListDerivation.messagePreview({ message, authorName: null, isOwnMessage: false, showsAuthorPrefix: false, youLabel: '', attachmentPlaceholder: '' });
    if (flattened.size > 4000) flattened.clear();
    flattened.set(message, out);
  }
  return out;
}

/** What the rows are built from — NOT the text being typed: highlights belong to the query the rows answered. */
export type RowsSource = Pick<SearchState, 'posts' | 'postsFor' | 'query' | 'usersById' | 'channelsById' | 'participants' | 'currentUserId'>;

export function buildRows(state: RowsSource, now: number): SearchResultsPresentation.Row[] {
  if (!state.posts.length) return [];
  const usersMap = new Map(Object.entries(state.usersById));
  const channelNames: Record<string, string> = {};
  for (const channel of Object.values(state.channelsById)) {
    channelNames[channel.id] = ChannelListDerivation.displayName(channel, {
      currentUserId: state.currentUserId,
      usersById: usersMap,
      participantIds: state.participants[channel.id] ?? [],
      unknownUserFallback: channel.type === 'G' ? t('channelList.unknownGroup') : t('channelList.unknownUser'),
    });
  }
  return SearchResultsPresentation.rows(
    {
      posts: state.posts,
      channelNames,
      usersById: state.usersById,
      query: SearchQuery.parse(state.postsFor || state.query),
      now,
      yesterdayLabel: t('channelList.time.yesterday'),
      attachmentPlaceholder: t('channelList.preview.attachment'),
      unknownAuthorLabel: t('channel.reaction.unknown'),
      unknownChannelLabel: t('mentions.unknownChannel'),
    },
    {
      // `messagePreview` with no author prefix IS the one-line flattening (+ the placeholder,
      // which `rows` applies itself to an empty result).
      flattenForOneLine,
      userDisplayName: ChannelListDerivation.userDisplayName,
      relativeTime: (epochMillis, nowMs, labels, timeZone) => RelativeTime.format(epochMillis, nowMs, labels, timeZone),
    },
  );
}

/** "YYYY-MM-DD" in the reader's calendar — the form `before:` / `after:` / `on:` take. */
export function isoDay(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => (n < 10 ? '0' + n : String(n));
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * A modifier chip's insertion: `key:` appended as its own token (a space first unless the text
 * already ends in one). Date modifiers come with today's date so the format is on screen, and the
 * date is returned as the SELECTION so typing replaces it. A trailing half-typed modifier of the
 * same key (`from:`) is not doubled.
 */
export function insertModifier(text: string, key: string, today: string): { text: string; selection: [number, number] } {
  const isDate = key === SearchQuery.keyBefore || key === SearchQuery.keyAfter || key === SearchQuery.keyOn;
  const token = key + ':';
  let base = text;
  if (base.endsWith(token)) base = base.slice(0, -token.length);
  if (base !== '' && !/\s$/u.test(base)) base += ' ';
  const value = isDate ? today : '';
  const out = base + token + value;
  return { text: out, selection: [base.length + token.length, out.length] };
}

/** The UTC offset (seconds, east positive) the server's `on:`/`before:`/`after:` need (§23.1). */
export function timeZoneOffsetSeconds(at = new Date()): number {
  return -at.getTimezoneOffset() * 60;
}
